import { Component, Input, Output, EventEmitter, OnInit } from "@angular/core";
import { FirmwareWidgetEnum } from "../../../enums/widget";
import { FirmwareDefinitionEntity } from "../../../entities/";
import { ToasterService } from "angular2-toaster";
import { FirmwareWidgetEventStruct } from "./firmware-widget-event.struct";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { ModalOptions } from "ngx-bootstrap/modal/modal-options.class";

@Component({
  selector: "firmware-definition-widget",
  template: `
    <ng-container [ngSwitch]="layout">
      <ng-container *ngSwitchCase="'table'">
        <div
          class="firmware-definition-widget"
          [ngClass]="{
            'div-table-tr': options?.index % 2 == 1,
            'div-table-tr-alt': options?.index % 2 == 0
          }"
        >
          <div class="row" (click)="showEditModal(defaultView, entity, $event)">
            <div class="col-3 text-left">#{{ entity?.identifier }}</div>
            <div class="col-3 text-left">{{ entity?.manufacturerId }}</div>
            <div class="col-3 text-left">{{ entity?.series }}</div>
            <div class="col-3 text-left">{{ entity?.firmwareVersion }}</div>
          </div>
        </div>
      </ng-container>

      <div class="firmware-definition-widget" *ngSwitchCase="'shadowbox'">
        <ng-container *ngTemplateOutlet="defaultView"></ng-container>
      </div>
      <div class="firmware-definition-widget" *ngSwitchDefault>
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div *ngIf="entity">
                <div class="card-header">
                  <strong>Firmware Definition:</strong>

                  <span> Version ({{ entity?.firmwareVersion }}) </span>

                  <span>
                    {{ getManufacturerDescription(entity?.manufacturerId) }}
                  </span>
                </div>
                <div class="card-body m-1 p-0">
                  <ng-container *ngTemplateOutlet="defaultView"></ng-container>
                </div>
                <div
                  class="card-footer"
                  *ngIf="entity?.pending && !options?.modal_form"
                >
                  <div class="row">
                    <div class="col-12">
                      <div class="alert alert-dark">
                        Caution: Saving changes to a firmware definition will
                        clear it's approval flag, blocking units from OTAing to
                        it if currently active!
                      </div>
                    </div>
                    <div class="col-12">
                      <button
                        (click)="save(entity, $event)"
                        class="btn btn-sm btn-danger"
                        type="submit"
                      >
                        <i class="fa fa-plus-circle"></i> Save Changes
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <ng-template #defaultView>
      <!-- Modal Header -->
      <div
        class="modal-header bg-light d-flex justify-content-between align-items-center"
      >
        <h4 class="modal-title m-0 h5">{{ currentModal?.title }}</h4>
        <button
          type="button"
          class="close pull-right"
          aria-label="Close"
          (click)="firmwareListModalRef.hide()"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="modal-body">
        <!-- Basic Information -->
        <div class="card mb-3">
          <div class="card-header bg-light">
            <h5 class="m-0">Basic Information</h5>
          </div>
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-md-3 font-weight-bold text-muted">Identifier:</div>
              <div class="col-md-9">
                {{ entity?.identifier }}
              </div>
            </div>

            <div class="row mb-2">
              <div class="col-md-3 font-weight-bold text-muted">Version:</div>
              <div class="col-md-9">
                <ng-container *ngIf="!options?.edit; else editVersion">
                  <strong>{{ entity?.firmwareVersion }}</strong>
                </ng-container>
                <ng-template #editVersion>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    [(ngModel)]="entity.firmwareVersion"
                    (ngModelChange)="updateDefinition($event)"
                  />
                </ng-template>
              </div>
            </div>

            <div class="row mb-2">
              <div class="col-md-3 font-weight-bold text-muted">Series:</div>
              <div class="col-md-9">
                <ng-container *ngIf="!options?.edit; else editSeries">
                  <strong>{{ entity?.series }}</strong>
                </ng-container>
                <ng-template #editSeries>
                  <ng-select
                    class="ng-select-sm"
                    [(ngModel)]="entity.series"
                    (ngModelChange)="updateDefinition($event)"
                    [items]="seriesSelectOptions"
                    bindLabel="label"
                    bindValue="value"
                  ></ng-select>
                </ng-template>
              </div>
            </div>

            <div class="row">
              <div class="col-md-3 font-weight-bold text-muted">Manufacturer:</div>
              <div class="col-md-9">
                <ng-container *ngIf="options?.edit">
                  <ng-select
                    class="ng-select-sm"
                    [(ngModel)]="entity.mid"
                    (ngModelChange)="updateManufacturer($event)"
                    [items]="manufacturerSelectOptions"
                    bindLabel="label"
                    bindValue="value"
                  ></ng-select>
                </ng-container>
                <ng-container *ngIf="!options?.edit">
                  {{ getManufacturerName(entity?.mid) }}
                </ng-container>
              </div>
            </div>
          </div>
        </div>

        <!-- Files Section -->
        <div class="card mb-3">
          <div class="card-header bg-dark text-white">
            <h5 class="m-0">Files</h5>
          </div>
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-md-3 font-weight-bold text-muted">Checksum:</div>
              <div class="col-md-9">
                <ng-container *ngIf="!options?.edit; else editChecksum">
                  <code>{{ entity?.checksum }}</code>
                </ng-container>
                <ng-template #editChecksum>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    [(ngModel)]="entity.checksum"
                    (ngModelChange)="updateDefinition($event)"
                  />
                </ng-template>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-3 font-weight-bold text-muted">Size:</div>
              <div class="col-md-9">
                <ng-container *ngIf="!options?.edit; else editSize">
                  <strong>{{ entity?.size }}</strong>
                </ng-container>
                <ng-template #editSize>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    [(ngModel)]="entity.size"
                    (ngModelChange)="updateDefinition($event)"
                  />
                </ng-template>
              </div>
            </div>

            <!-- File A/B or Single File based on conditions -->
            <div
              *ngIf="entity?.series !== 'V4' && entity?.manufacturerId != '15'"
            >
              <div class="card mb-2">
                <div class="card-header bg-light">
                  <h6 class="m-0">File A (User 1)</h6>
                </div>
                <div class="card-body p-2">
                  <ng-container *ngIf="!options?.edit; else editFileA">
                    <code>{{ entity?.fileA }}</code>
                  </ng-container>
                  <ng-template #editFileA>
                    <input
                      type="text"
                      class="form-control"
                      [(ngModel)]="entity.fileA"
                      (ngModelChange)="updateDefinition($event)"
                    />
                  </ng-template>
                </div>
              </div>

              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="m-0">File B (User 2)</h6>
                </div>
                <div class="card-body p-2">
                  <ng-container *ngIf="!options?.edit; else editFileB">
                    <code>{{ entity?.fileB }}</code>
                  </ng-container>
                  <ng-template #editFileB>
                    <input
                      type="text"
                      class="form-control"
                      [(ngModel)]="entity.fileB"
                      (ngModelChange)="updateDefinition($event)"
                    />
                  </ng-template>
                </div>
              </div>
            </div>

            <div
              *ngIf="entity?.series === 'V4' || entity?.manufacturerId == '15'"
            >
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="m-0">File</h6>
                </div>
                <div class="card-body p-2">
                  <ng-container *ngIf="!options?.edit; else editFile">
                    <code>{{ entity?.file }}</code>
                  </ng-container>
                  <ng-template #editFile>
                    <input
                      type="text"
                      class="form-control"
                      [(ngModel)]="entity.file"
                      (ngModelChange)="updateDefinition($event)"
                    />
                  </ng-template>
                </div>
              </div>
            </div>

            <!-- Security Certificate -->
            <div *ngIf="entity?.manufacturerId == '15'" class="card mt-2">
              <div class="card-header bg-light">
                <h6 class="m-0">Security Storage Certificate</h6>
              </div>
              <div class="card-body p-2">
                <ng-container *ngIf="!options?.edit; else editSecurityCert">
                  <code>{{ entity?.security_certificate }}</code>
                </ng-container>
                <ng-template #editSecurityCert>
                  <input
                    type="text"
                    class="form-control"
                    [(ngModel)]="entity.security_certificate"
                    (ngModelChange)="updateDefinition($event)"
                  />
                </ng-template>
              </div>
            </div>
          </div>
        </div>

        <!-- Release Notes -->
        <div class="card mb-3">
          <div class="card-header">
            <h5 class="m-0">Release Notes</h5>
          </div>
          <div class="card-body">
            <ng-container *ngIf="!options?.edit; else editNotes">
              <div class="p-2 bg-light rounded" *ngIf="entity?.notes">
                {{ entity?.notes }}
              </div>
              <div class="text-muted" *ngIf="!entity?.notes">
                No release notes available
              </div>
            </ng-container>
            <ng-template #editNotes>
              <textarea
                class="form-control"
                rows="5"
                [(ngModel)]="entity.notes"
                (ngModelChange)="updateDefinition($event)"
              ></textarea>
            </ng-template>
          </div>
        </div>

        <!-- Attributes -->
        <div class="card mb-3">
          <embed-device-attributes
            [entity]="entity"
            [layout]="'card'"
            [options]="
              options?.attributes || {
                title: 'Attributes',
                edit: options?.edit
              }
            "
          ></embed-device-attributes>
        </div>

        <!-- Admin Notes -->
        <div class="card mb-3" *ngIf="entity?.identifier">
          <entity-notes-v2
            [title]="'Firmware Admin Notes'"
            [entity]="entity.sref()"
            [options]="{ expand: true, dragula: false }"
            [placeholder]="'Firmware Staff Note.'"
          ></entity-notes-v2>
        </div>

        <!-- Pending Warning -->
        <div *ngIf="entity?.pending" class="alert alert-warning">
          <i class="fas fa-exclamation-triangle me-2"></i>
          Caution: Saving changes to a firmware definition will clear its
          approval flag, blocking units from OTAing to it if currently active!
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer bg-light">
        <button
          *ngIf="currentModal?.cancelMsg"
          type="button"
          class="btn btn-outline-secondary"
          (click)="currentModal?.cancel()"
          data-dismiss="modal"
        >
          {{ currentModal?.cancelMsg }}
        </button>
        <button
          *ngIf="currentModal?.deleteMsg"
          type="button"
          class="btn btn-outline-danger"
          (click)="currentModal?.delete()"
          data-dismiss="modal"
        >
          {{ currentModal?.deleteMsg }}
        </button>
        <button
          *ngIf="currentModal?.revertMsg"
          type="button"
          class="btn btn-outline-warning"
          (click)="currentModal?.revert()"
          data-dismiss="modal"
        >
          {{ currentModal?.revertMsg }}
        </button>
        <button
          *ngIf="currentModal?.confirmMsg && entity?.pending"
          type="button"
          class="btn btn-primary"
          (click)="currentModal?.confirm()"
        >
          {{ currentModal?.confirmMsg }}
        </button>
      </div>
    </ng-template>
  `,
})
export class FirmwareDefinitionWidgetComponent implements OnInit {
  @Input() entity: FirmwareDefinitionEntity | null = null;
  @Input() options: any = null;
  @Input() layout = "standard";
  @Input() manufacturerSelectOptions: any = null;

  @Output() widgetEvent = new EventEmitter<FirmwareWidgetEventStruct>();
  forwardEvent(e, widgetEvent: FirmwareWidgetEventStruct) {
    e.preventDefault();
    this.widgetEvent.emit(widgetEvent);
  }
  public firmwareListModalRef: BsModalRef;
  FirmwareWidgetEnum = FirmwareWidgetEnum;
  manufacturerId: string;

  constructor(
    public toasterService: ToasterService,
    public modalService: BsModalService
  ) {}

  public expand = false;
  public currentModal: any;

  seriesSelectOptions = [
    { label: "V2", value: "V2" },
    { label: "V3", value: "V3" },
    { label: "V4", value: "V4" },
  ];

  save(record: FirmwareDefinitionEntity, event) {
    event.preventDefault();
    record.update();
  }

  updateManufacturer(e) {
    this.entity.manufacturerId = Number(this.entity.mid);
    this.updateDefinition(e);
  }

  ngOnInit() {
    this.entity.mid = String(this.entity.manufacturerId);
  }

  public editTypeModal = {
    title: "Firmware Definition",
    msg: `Edit Firmware Definition.`,
    errorMsg: null,
    processing: false,
    form: "edit-attribute",
    confirmMsg: "Save",
    confirm: () => {
      delete this.entity.mid;
      if (this.entity.update()) {
        this.firmwareListModalRef.hide();
        this.editTypeModal.errorMsg = null;
      }
    },
    cancelMsg: "Cancel",
    cancel: () => {
      this.editTypeModal.errorMsg = null;
      this.firmwareListModalRef.hide();
    },
  };

  public showEditModal(modal, entry: FirmwareDefinitionEntity, event) {
    event.preventDefault();
    this.options.edit = true;
    this.currentModal = this.editTypeModal;
    let config: ModalOptions = {
      backdrop: "static",
      keyboard: false,
      class: "lg-modal",
    };
    this.firmwareListModalRef = this.modalService.show(modal, config);
  }

  getManufacturerDescription(manufacturerId: number): string | null {
    const option = this.manufacturerSelectOptions.find(
      (opt) => opt.value === manufacturerId
    );
    return option ? option.description : null;
  }

  updateDefinition(event) {
    this.entity.pending = true;
  }
}
